import React, { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, FormControl, InputLabel, Select, MenuItem, TextField, CircularProgress, FormHelperText } from '@mui/material';
import config from '../../../config';
import { authFetch, handleApiResponse } from '../../../api/authFetch';
import { describeAdminError } from '../../../utils/adminErrors';
import { isSchoolScopedRole } from '../../../constants/roles';
import { TOUCH_HIT_SX } from '../../../theme/adminTheme';

// Single-school picker fed by GET /api/admin/schools — the same endpoint
// and name-dedupe/sort UsersView.js already uses for its per-row school
// Select, pulled out here so every other admin surface that needs a school
// picker (filters, forms) shares one fetch implementation instead of
// growing its own copy.
//
// currentUserRole / lockedSchoolName: a SCHOOL_ADMIN or TEACHER is scoped
// to exactly one school (see isSchoolScopedRole in constants/roles.js) —
// for those roles this renders a disabled field showing that school
// instead of a live dropdown, mirroring UsersView's row-level Select
// (`disabled={isSchoolScopedRole(currentUserRole)}`) and the Add User
// dialog's School field in AdminDashboardV2.js.
export default function SchoolPicker({
  value,
  onChange,
  currentUserRole,
  lockedSchoolName,
  label = 'School',
  required = false,
  allowUnassigned = true,
  // Text for the empty-value option — "Unassigned" reads correctly when
  // this picker is setting a specific user's school (the empty value means
  // "this person has no school"), but is misleading when the same picker
  // is reused as a list filter (the empty value there means "don't filter
  // by school at all", not "only show unassigned users"). Callers filtering
  // a list should pass e.g. `emptyLabel="All schools"`; every other caller
  // gets the original wording with no change.
  emptyLabel = 'Unassigned',
  size = 'small',
  fullWidth = true,
  sx,
}) {
  const navigate = useNavigate();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Was a hardcoded "school-picker-label" — a real bug whenever two
  // SchoolPickers are ever in the DOM together (e.g. a list filter plus a
  // form dialog on the same page): duplicate ids make `labelId` ambiguous,
  // which is exactly the kind of thing that produces unpredictable
  // label/value rendering. One id per instance instead.
  const labelId = useId();

  const locked = isSchoolScopedRole(currentUserRole);

  useEffect(() => {
    if (locked) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    authFetch(`${config.API_BASE_URL}/api/admin/schools`)
      .then((res) => handleApiResponse(res, navigate))
      .then((data) => {
        if (cancelled || data === null) return; // null: 401 — already redirected
        const names = (data.schools || []).map((s) => (s.name || '').trim()).filter(Boolean);
        setOptions([...new Set(names)].sort((a, b) => a.localeCompare(b)));
      })
      .catch((err) => {
        if (cancelled) return;
        setOptions([]);
        setError(describeAdminError(err, "Couldn't load schools."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, locked]);

  if (locked) {
    return (
      <TextField
        label={label}
        value={lockedSchoolName || ''}
        fullWidth={fullWidth}
        size={size}
        disabled
        helperText="Scoped to your school automatically."
        sx={{ '& .MuiOutlinedInput-root': TOUCH_HIT_SX, ...sx }}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
      <FormControl fullWidth={fullWidth} size={size} error={Boolean(error)} required={required}>
        {/* `shrink` explicitly, rather than MUI's default (auto-shrink
            only when `value` is truthy): with `displayEmpty` on, the Select
            always renders visible content in the value slot — a real
            school name, or the empty-option's `emptyLabel` ("All schools"/
            "Unassigned") — even while `value` is "". MUI's own shrink
            logic only looks at `value` truthiness, not at whether
            displayEmpty is making something visible anyway, so with the
            filter at its default ("" / unfiltered) the label never moved
            out of the way and sat drawn directly on top of that emptyLabel
            text. Not shrinking is still correct when `allowUnassigned` is
            false — an empty, non-displayEmpty Select shows no value text
            to collide with. */}
        <InputLabel id={labelId} shrink={allowUnassigned || Boolean(value)}>
          {label}
        </InputLabel>
        <Select
          labelId={labelId}
          label={label}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          displayEmpty={allowUnassigned}
          disabled={loading}
          sx={{ '& .MuiSelect-select': TOUCH_HIT_SX }}
        >
          {allowUnassigned && (
            <MenuItem value="">
              <em>{emptyLabel}</em>
            </MenuItem>
          )}
          {options.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
      {loading && <CircularProgress size={18} aria-label="Loading schools" />}
    </Box>
  );
}
